import { Box, SxProps, Theme, Typography, styled } from '@mui/material';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import React from 'react';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
    UseFormStateReturn,
    useFormContext,
} from 'react-hook-form';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

interface UploadFileAreaProps {
    name: string;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
    accept?: Record<string, string[]>;
}
const getDropzoneColor = (props: DropzoneRootProps, theme: Theme) => {
    if (props.isDragAccept) {
        return theme.palette.success.main;
    }
    if (props.isDragReject) {
        return theme.palette.error.main;
    }
    if (props.isFocused) {
        return theme.palette.primary.main;
    }
    return colors.main;
};
const Dropzone = styled('div')(({ theme, ...props }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '15rem',
    height: '15rem',
    borderColor: getDropzoneColor(props, theme),
    backgroundColor: common.white,
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
}));
const UploadFileArea: React.FC<UploadFileAreaProps> = ({
    name,
    multiple,
    disabled,
    sx,
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState, formState }) => (
                <FileUploadRender
                    field={field}
                    fieldState={fieldState}
                    formState={formState}
                    sx={sx}
                    multiple={multiple}
                    disabled={disabled}
                />
            )}
        />
    );
};

const FileUploadRender = ({
    field,
    multiple,
    disabled,
    sx,
}: {
    field: ControllerRenderProps<FieldValues, string>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<FieldValues>;
    sx?: SxProps;
    multiple?: boolean;
    disabled?: boolean;
}) => {
    const onDrop = (acceptedFiles: File[]) => {
        if (multiple) {
            field.onChange([...(field.value || []), ...acceptedFiles]);
        } else {
            field.onChange(acceptedFiles[0]);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        onDrop,
        multiple: multiple,
        disabled: disabled,
    });

    const renderFile = (file: File | string) => {
        if (file instanceof File) {
            return <>{file.name}</>;
        }
    };
    return (
        <>
            <Dropzone
                {...getRootProps({
                    isFocused,
                    isDragAccept,
                    isDragReject,
                    sx: {
                        ...sx,
                        borderRadius: '8px',
                    },
                })}
            >
                <input {...getInputProps()} />
                {multiple
                    ? field?.value?.map((file: File) => renderFile(file))
                    : field?.value && renderFile(field?.value)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 104, color: 'var(--color-primary)' }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            sx={{
                                fontSize: '18px',
                                color: '#494949',
                                cursor: 'pointer',
                            }}
                        >
                            Thả file tại đây hoặc {''}
                            <b style={{ fontWeight: 700, color: colors.main }}>
                                Tải tệp từ máy tính
                            </b>
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '15px',
                                fontWeight: '300',
                                color: '#737373',
                                fontStyle: 'italic',
                            }}
                        >
                            Định dạng .xlsx và không quá 5MB
                        </Typography>
                    </Box>
                </Box>
            </Dropzone>
        </>
    );
};

export default UploadFileArea;
